import {Store} from "./models";
import {html} from "lit";
import {i18nService} from "../../services/i18nService";
import favouriteStoreIcon from "../../assets/images/png/favouriteStore.png";
import {markerController} from "./marker";
import {storeService} from "../../services/store-service";

class StoreController {

  // =========================
  // 🔹 UTILITY FUNCTIONS
  // =========================

  getStoreById(id: string | undefined, stores: Store[]) {
    return stores.find(it => it.id === id);
  }

  setMyStore(myStoreId: string, stores: Store[]) {
    const store = this.getStoreById(myStoreId, stores);
    if (!store) {
      return;
    }
    store.isFavourite = true;
    store.marker.content = markerController.createMarkerContent(favouriteStoreIcon, 50);
    return store;
  }

  // =========================
  // 🔹 DISTANCE & BOUNDS
  // =========================

  updateStoreDistance(stores: Store[], pos: google.maps.LatLng) {
    return stores.map(store => {
      const markerPosition = store.marker.position;
      if (!markerPosition) {
        return store;
      }
      const distance = google.maps.geometry.spherical.computeDistanceBetween(pos, markerPosition);
      const km = distance / 1000;
      store.numberDistance = distance;
      store.stringDistance = km.toLocaleString("de-DE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }) + " km";
      return store;
    }).sort((a, b) => a.numberDistance - b.numberDistance);
  }

  createBoundsForClosestStore(store: Store, pos: google.maps.LatLng) {
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(pos);
    bounds.extend(store?.marker.position as google.maps.LatLng);
    return bounds;
  }

  getClosestStores(slice: number, stores: Store[]) {
    return stores.slice(0, slice);
  }

  // =========================
  // 🔹 MAP INTERACTION
  // =========================

  panToLocation(zoom: number, position?: any, map?: google.maps.Map) {
    if (map && position) {
      map.panTo(position);
      map.setZoom(zoom);
    }
  }

  // =========================
  // 🔹 OPENING HOURS STATUS
  // =========================

  checkStoreOpenings(store: Store) {
    const now = new Date();
    const currentHour = now.getHours();
    const dayIndex = new Date().getDay() - 1;

    if (!store.openings[dayIndex]) {
      return;
    }

    let status, color;
    const openingHour = parseInt(store.openings[dayIndex].opening.substring(0, 2));
    const closingHour = parseInt(store.openings[dayIndex].closing.substring(0, 2));

    if (currentHour >= openingHour && currentHour < closingHour) {
      if (closingHour - currentHour === 1) {
        status = i18nService?.translate('store.closing');
        color = "orange";
      } else {
        status = i18nService?.translate('store.open');
        color = "green";
      }
    } else if (currentHour < openingHour) {
      if (openingHour - currentHour === 1) {
        status = i18nService?.translate('store.opening');
        color = "orange";
      } else {
        status = i18nService?.translate('store.closed');
        color = "red";
      }
    }

    return html`<p style="font-weight: 400" class=${color}>${status}</p>`;
  }

  // =========================
  // 🔹 STORE SELECTION
  // =========================

  selectStore(
    store: Store,
    currentStore: Store | undefined,
    map: google.maps.Map | undefined,
    setCurrentStore: (store: Store) => void,
    showInfoBox: () => void
  ) {
    markerController.handleMarkerChanged(store, currentStore);
    this.panToLocation(18, store.marker.position, map);
    showInfoBox();
    setCurrentStore(store);
  }

  // =========================
  // 🔹 FAVOURITE STORE HANDLING
  // =========================

  subscribeToFavouriteStore(
    stores: Store[],
    map: google.maps.Map | undefined,
    favouriteId: string | undefined,
    setFavouriteId: (id: string) => void,
    setCurrentStore: (store: (Store | undefined)) => void
  ) {
    storeService.subscribeForMyStoreId((profileStoreId: string) => {
      const store = this.setMyStore(profileStoreId, stores);

      if (store) {
        this.panToLocation(18, store.marker.position, map);
        setCurrentStore(store);
        setFavouriteId(store.id);
      } else {
        markerController.resetStore(stores, favouriteId);
        setFavouriteId("");
      }
    });
  }

  setFavouriteStore(
    store: Store | undefined,
    isLoggedIn: () => boolean,
    favouriteId: string | undefined,
    stores: Store[],
    setFavouriteId: (id: string) => void,
    showLoginPrompt: () => void
  ) {
    if (!isLoggedIn()) {
      showLoginPrompt();
      return;
    }

    if (store?.id === favouriteId) {
      markerController.resetStore(stores, favouriteId);
      storeService.saveMyStore("");
      setFavouriteId("");
    } else if (store) {
      storeService.storeCallback(store, store.id);
      storeService.saveMyStore(store.id);
      markerController.resetStore(stores, favouriteId);
      setFavouriteId(store.id);
    }
  }

  // =========================
  // 🔹 STORE SEARCH
  // =========================

  filterStoresBySearchTerm(
    stores: Store[],
    searchInput: string,
    favouriteId?: string
  ): Store[] {
    if (!searchInput) {
      return [];
    }

    const searchTermsArray = searchInput.trim().split(/\s+/);

    let filteredStores = stores.filter(store =>
      searchTermsArray.every(term =>
        `${store.address_line_1} ${store.address_line_2} ${store.address_line_3}`
          .toLowerCase()
          .includes(term.toLowerCase())
      )
    );

    if (favouriteId) {
      const favStore = stores.find(store => store.id === favouriteId);
      if (favStore) {
        filteredStores = filteredStores.filter(store => store.id !== favouriteId);
        filteredStores.unshift(favStore);
      }
    }

    return filteredStores;
  }
}

export const storeController = new StoreController();
